import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { HelpCircle, Phone, Mail, MapPin, ShieldCheck, Truck, AlertTriangle, Building2, Handshake, Send, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import Spinner from "../components/Spinner";

const PAGES = {
  about: {
    icon: Building2,
    title: "À propos de nous",
    body: [
      "John Elektwo est une boutique haïtienne basée à Gonaïves, spécialisée dans l'électronique, l'électroménager et les solutions d'énergie solaire.",
      "Notre mission : offrir aux familles haïtiennes des produits de qualité au juste prix, livrés rapidement, avec un service après-vente accessible directement sur WhatsApp.",
    ],
  },
  contact: {
    icon: Phone,
    title: "Contactez-nous",
    body: [
      "Adresse : Gonaïves, Haïti",
      "Téléphone / WhatsApp : +509 32190716",
      "Email : Contact@johnelektwo.com",
      "Horaires : Lundi - Samedi, 8h - 18h",
    ],
  },
  help: {
    icon: HelpCircle,
    title: "Centre d'aide",
    body: [
      "Comment passer commande ? Choisissez vos produits, ajoutez-les au panier puis suivez le checkout en saisissant votre adresse de livraison.",
      "Comment suivre ma commande ? Utilisez votre numéro de suivi (JE-XXXXXX) sur la page Suivi.",
      "Modes de paiement : démo (à la livraison ou via accord WhatsApp). Plus de moyens bientôt.",
      "Pour toute question, contactez-nous sur WhatsApp : +509 32190716",
    ],
  },
  security: {
    icon: ShieldCheck,
    title: "Centre de sécurité",
    body: [
      "Vos données sont chiffrées et stockées de manière sécurisée.",
      "Ne partagez jamais votre mot de passe. Notre équipe ne vous le demandera jamais.",
      "En cas d'activité suspecte sur votre compte, contactez-nous immédiatement.",
    ],
  },
  partner: {
    icon: Handshake,
    title: "Devenir partenaire JE",
    body: [
      "Vous êtes fournisseur, distributeur ou installateur ? Rejoignez notre réseau de partenaires.",
      "Avantages : visibilité sur la plateforme, commissions sur les ventes, support marketing.",
      "Envoyez votre dossier à Contact@johnelektwo.com",
    ],
  },
  "info/livraison": {
    icon: Truck,
    title: "Informations de livraison",
    body: [
      "Nord, Nord-Est, Artibonite : 2 à 5 jours ouvrables.",
      "Ouest, Nord-Ouest, Centre : 5 à 15 jours ouvrables.",
      "Autres départements : délai à confirmer par notre équipe.",
      "Nous proposons aussi des points de livraison personnalisés à la demande — contactez-nous via WhatsApp.",
    ],
  },
  "info/retour": {
    icon: ShieldCheck,
    title: "Politique de retour & remboursement",
    body: [
      "Vous disposez de 7 jours après la livraison pour signaler un défaut.",
      "Les produits doivent être retournés dans leur emballage d'origine, non utilisés.",
      "Le remboursement est effectué sous 14 jours après réception et inspection du produit.",
    ],
  },
  "info/signaler": {
    icon: AlertTriangle,
    title: "Signaler une activité suspecte",
    body: [
      "Si vous remarquez une connexion inconnue, un message frauduleux ou un comportement anormal, contactez-nous immédiatement.",
      "Email : Contact@johnelektwo.com",
      "WhatsApp : +509 32190716",
    ],
  },
};

export default function InfoPage() {
  const loc = useLocation();
  const slug = loc.pathname.replace(/^\//, "");
  const data = PAGES[slug];
  if (!data) {
    return (
      <div className="je-container py-20 text-center">
        <h1 className="text-2xl font-bold">Page introuvable</h1>
        <Link to="/" className="text-[#0052CC] hover:underline mt-3 inline-block">Retour à l'accueil</Link>
      </div>
    );
  }
  const Icon = data.icon;
  return (
    <div className="je-container py-12 max-w-3xl" data-testid={`info-page-${slug}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-[#0052CC]/10 flex items-center justify-center"><Icon className="w-6 h-6 text-[#0052CC]" /></div>
        <h1 className="text-3xl md:text-4xl font-bold" style={{fontFamily:"Outfit"}}>{data.title}</h1>
      </div>
      <div className="space-y-4">
        {data.body.map((p, i) => <p key={i} className="text-gray-700 leading-relaxed">{p}</p>)}
      </div>
      {slug === "contact" && (
        <div className="mt-8 bg-gradient-to-br from-[#0052CC] to-[#001A5C] text-white rounded-2xl p-5 md:p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="become-ambassador-cta">
          <div>
            <p className="text-xs uppercase tracking-widest font-bold opacity-80">Programme Ambassadeur</p>
            <p className="text-lg md:text-xl font-extrabold mt-1" style={{ fontFamily: "Outfit" }}>Vous êtes artiste, influenceur ou promoteur ?</p>
            <p className="opacity-90 text-sm">Rejoignez notre programme d'ambassadeurs et gagnez une commission sur chaque commande.</p>
          </div>
          <Link to="/become-ambassador" className="bg-white text-[#0052CC] font-bold px-5 py-2.5 rounded-lg hover:bg-white/90 whitespace-nowrap" data-testid="become-ambassador-btn">
            Devenir ambassadeur JohnElektwo →
          </Link>
        </div>
      )}
      {slug === "contact" && <ContactForm />}
      <div className="mt-10 bg-[#F3F4F6] rounded-xl p-5 text-sm">
        <p className="font-semibold flex items-center gap-2"><MapPin className="w-4 h-4" /> Gonaïves, Haïti</p>
        <p className="mt-1 flex items-center gap-2"><Mail className="w-4 h-4" /> Contact@johnelektwo.com</p>
        <p className="mt-1 flex items-center gap-2"><Phone className="w-4 h-4" /> +509 32190716</p>
      </div>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message envoyé — nous vous répondrons sous 24h");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mt-10" data-testid="contact-form-section">
      <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Envoyez-nous un message</h2>
      <p className="text-sm text-gray-500 mb-5">Remplissez ce formulaire — nous vous répondrons directement par email.</p>

      {sent && (
        <div className="mb-5 flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4" data-testid="contact-success">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-green-800">Message bien reçu !</p>
            <p className="text-green-700 mt-0.5">Notre équipe vous répondra dans les meilleurs délais à l'adresse email fournie.</p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="bg-white border rounded-2xl p-5 md:p-6 shadow-sm space-y-4" data-testid="contact-form">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Nom complet *</label>
            <input
              required
              minLength={2}
              maxLength={120}
              value={form.name}
              onChange={upd("name")}
              placeholder="Jean Dupont"
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition"
              data-testid="contact-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Email *</label>
            <input
              required
              type="email"
              maxLength={200}
              value={form.email}
              onChange={upd("email")}
              placeholder="votre@email.com"
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition"
              data-testid="contact-email"
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-500">Sujet *</label>
          <input
            required
            minLength={2}
            maxLength={200}
            value={form.subject}
            onChange={upd("subject")}
            placeholder="Ex : Question sur une commande"
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition"
            data-testid="contact-subject"
          />
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-500">Message *</label>
          <textarea
            required
            minLength={10}
            maxLength={5000}
            rows={6}
            value={form.message}
            onChange={upd("message")}
            placeholder="Décrivez votre demande…"
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 mt-1 focus:border-[#0052CC] focus:outline-none transition resize-y"
            data-testid="contact-message"
          />
          <p className="text-[11px] text-gray-400 mt-1 text-right">{form.message.length} / 5000</p>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="je-btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="contact-submit"
        >
          {loading ? <Spinner className="w-4 h-4" /> : <Send className="w-4 h-4" />}
          {loading ? "Envoi…" : "Envoyer le message"}
        </button>
        <p className="text-[11px] text-gray-400">Ou contactez-nous directement sur WhatsApp au <b>+509 32190716</b>.</p>
      </form>
    </section>
  );
}
